import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import {
  userSignUpApi,
  getUserProfilepApi,
  updateUserProfileApi,
  updateUserPasswordApi,
  ForgotPasswordApi,
} from './auth.api';
import { setToken } from '../../helper/localStorage';
import { setHeader } from '../../app/axios';
import { actions as appActions } from '../app/app.slice';
import { getSettingsAsync } from '../settings/settings.slice';
import { IPermissions, filterRolePermissions } from '../../app/permission';
import { ApiError, IbaseDoc } from '../../components/types';

export interface IAuthState {
  user: IUser;
  permissions: IPermissions;
  isAuthenticated: boolean;
  loading: boolean;
  forgotPassword: any;
  forgotPasswordSuccess: boolean;
  error: ApiError;
}
export const USER_ROLES = [
  'member',
  'customer',
  'user',
  'subadmin',
  'admin',
  'super-admin',
] as const;
export type Role = typeof USER_ROLES[number];
export type Status = 'active' | 'deleted' | 'suspend';

export interface Profile {
  name: string;
  contactNumber?: string;
  gender?: string;
  location?: string;
  website?: string;
  picture?: string;
  provider?: string;
  providerId?: string;
  locale?: string;
}

export interface AuthToken {
  kind: string;
  accessToken: string;
  refreshToken: string;
}

export interface IUser extends IbaseDoc {
  email: string;
  password?: string;
  passwordResetToken?: string;
  passwordResetExpires?: Date;
  emailToken?: string;
  emailTokenExpires?: Date;
  isVerified?: boolean;
  verifiedAt?: Date;
  isFirstTimeUser?: boolean;
  accessToken?: string;
  role?: Role;
  profile: Profile;
  parentId?: IUser;
  status?: Status;
  facebook?: string;
  google?: string;
  tokens?: AuthToken[];
}

export const emptyUser: IUser = {
  id: '',
  _id: '',
  email: '',
  profile: { name: '' },
};

const initialState: IAuthState = {
  user: emptyUser,
  isAuthenticated: false,
  permissions: {},
  loading: false,
  forgotPassword: null,
  forgotPasswordSuccess: false,
  error: null,
};

export const logInAsync = createAsyncThunk(
  'auth/logIn',
  async (data: any, { rejectWithValue, dispatch }) => {
    // const { rejectWithValue } = thunkAPI;
    try {
      const response = await userSignUpApi(data);
      if (response.status === 200) {
        const token = response?.data?.data?.token;
        await setHeader(token);
        await setToken(token);
        dispatch(getProfileAsync(null));
      }
      return response.data;
    } catch (error: ApiError) {
      dispatch(
        appActions.triggerAlert({
          type: 'error',
          childern: error?.response?.data?.error || 'error_occurred',
        })
      );
      return rejectWithValue(error?.response?.data);
    }
  }
);
export const getProfileAsync = createAsyncThunk(
  'auth/profile',
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      dispatch(appActions.appLoadingStart());
      dispatch(getSettingsAsync(null)); // load MM settings for all users - public

      const response = await getUserProfilepApi();

      const permissions = filterRolePermissions(response?.data?.data?.role);
      dispatch(actions.updatePermission(permissions));
      // dispatch(appActions.appLoadingCompleted());
      return response.data;
    } catch (error: ApiError) {
      await setToken('');
      return rejectWithValue(error?.response?.data);
    } finally {
      dispatch(appActions.appLoadingCompleted());
    }
  }
);

export const updateProfileAsync = createAsyncThunk(
  'auth/profile/update',
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await updateUserProfileApi(data);
      dispatch(
        appActions.triggerAlert({
          type: 'success',
          childern: 'successfully_updated_profile',
        })
      );
      return response.data;
    } catch (error: ApiError) {
      dispatch(
        appActions.triggerAlert({
          type: 'error',
          childern: error?.response?.data?.error || 'error_occurred',
        })
      );
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateUserPasswordAsync = createAsyncThunk(
  'auth/password/update',
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await updateUserPasswordApi(data);
      dispatch(
        appActions.triggerAlert({
          type: 'success',
          childern: 'successfully_updated_password',
        })
      );
      return response.data;
    } catch (error: ApiError) {
      dispatch(
        appActions.triggerAlert({
          type: 'error',
          childern: error?.response?.data?.error || 'error_occurred',
        })
      );
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const ForgotPasswordAsync = createAsyncThunk(
  'auth/forgotPassword',
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await ForgotPasswordApi(data);
      dispatch(
        appActions.triggerAlert({
          type: 'success',
          childern: 'password_reset_email_sent',
        })
      );
      return response.data;
    } catch (error: ApiError) {
      dispatch(
        appActions.triggerAlert({
          type: 'error',
          childern: error?.response?.data?.error || 'error_occurred',
        })
      );
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    signOut(state) {
      state.user = emptyUser;
      state.isAuthenticated = false;
      state.permissions = {};
    },
    updatePermission(state, action) {
      state.permissions = action?.payload;
    },
  },
  extraReducers: (builder) => {
    // signin
    builder.addCase(logInAsync.pending, (state, action) => {
      state.loading = true;
      state.isAuthenticated = false;
    });
    builder.addCase(logInAsync.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(logInAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // get profile
    builder.addCase(getProfileAsync.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getProfileAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action?.payload?.data;
      state.isAuthenticated = true;
    });
    builder.addCase(getProfileAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    });

    // update profile
    builder.addCase(updateProfileAsync.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(updateProfileAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action?.payload?.data;
      state.isAuthenticated = true;
    });
    builder.addCase(updateProfileAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    });

    // forgot password
    builder.addCase(ForgotPasswordAsync.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(ForgotPasswordAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.forgotPassword = action?.payload?.data;
      state.forgotPasswordSuccess = action?.payload?.success;
    });
    builder.addCase(ForgotPasswordAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const { actions } = authSlice;

export const selectAuth = (state: RootState): IAuthState => state.auth;

export default authSlice.reducer;
